import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Warehouse as WarehouseIcon,
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  X,
  Building,
  Package,
  Users,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Pause,
  LayoutGrid,
  List
} from 'lucide-react';
import { warehouseService } from '../../../services/warehouseService';
import { branchService } from '../../../services/branchService';

const Warehouses = () => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState('table');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterBranch, setFilterBranch] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [viewingWarehouse, setViewingWarehouse] = useState(null);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    branchId: '',
    warehouseType: 'GENERAL',
    status: 'ACTIVE',
    description: '',
    capacity: '',
    capacityUnit: 'UNITS',
    currentUtilization: '',
    managerName: '',
    managerEmail: '',
    managerPhone: '',
    notes: ''
  });

  const [warehouses, setWarehouses] = useState([]);

  const warehouseTypes = [
    { value: 'GENERAL', label: t('cmms.wh_general') },
    { value: 'RAW_MATERIALS', label: t('cmms.wh_rawMaterials') },
    { value: 'FINISHED_GOODS', label: t('cmms.wh_finishedGoods') },
    { value: 'SPARE_PARTS', label: t('cmms.wh_spareParts') },
    { value: 'COLD_STORAGE', label: t('cmms.wh_coldStorage') },
    { value: 'HAZMAT', label: t('cmms.wh_hazmat') },
    { value: 'TOOLS', label: t('cmms.wh_tools') },
    { value: 'CONSUMABLES', label: t('cmms.wh_consumables') }
  ];

  const statusOptions = [
    { value: 'ACTIVE', label: t('common.active') },
    { value: 'INACTIVE', label: t('common.inactive') },
    { value: 'FULL', label: t('cmms.wh_full') },
    { value: 'MAINTENANCE', label: t('cmms.wh_maintenance') },
    { value: 'CLOSED', label: t('cmms.wh_closed') }
  ];

  const capacityUnits = [
    { value: 'UNITS', label: t('cmms.wh_units') },
    { value: 'KG', label: t('cmms.wh_kg') },
    { value: 'LBS', label: t('cmms.wh_lbs') },
    { value: 'LITERS', label: t('cmms.wh_liters') },
    { value: 'CUBIC_METERS', label: t('cmms.wh_cubicMeters') },
    { value: 'PALLETS', label: t('cmms.wh_pallets') }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [warehousesData, branchesData] = await Promise.all([
        warehouseService.getWarehouses(),
        branchService.getBranches()
      ]);
      setWarehouses(warehousesData || []);
      setBranches(branchesData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      setWarehouses([]);
      setBranches([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      ACTIVE: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: t('common.active') },
      INACTIVE: { color: 'bg-gray-100 text-gray-800', icon: Pause, label: t('common.inactive') },
      FULL: { color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle, label: t('cmms.wh_full') },
      MAINTENANCE: { color: 'bg-orange-100 text-orange-800', icon: AlertTriangle, label: t('cmms.wh_maintenance') },
      CLOSED: { color: 'bg-red-100 text-red-800', icon: XCircle, label: t('cmms.wh_closed') }
    };
    return statusMap[status] || statusMap.ACTIVE;
  };

  const getTypeBadge = (type) => {
    const typeMap = {
      GENERAL: 'bg-blue-100 text-blue-800',
      RAW_MATERIALS: 'bg-amber-100 text-amber-800',
      FINISHED_GOODS: 'bg-green-100 text-green-800',
      SPARE_PARTS: 'bg-purple-100 text-purple-800',
      COLD_STORAGE: 'bg-cyan-100 text-cyan-800',
      HAZMAT: 'bg-red-100 text-red-800',
      TOOLS: 'bg-indigo-100 text-indigo-800',
      CONSUMABLES: 'bg-pink-100 text-pink-800'
    };
    return typeMap[type] || 'bg-gray-100 text-gray-800';
  };

  const getTypeLabel = (type) => {
    const found = warehouseTypes.find(t => t.value === type);
    return found ? found.label : type;
  };

  const getUtilizationColor = (current, max) => {
    if (!max || max === 0) return 'bg-gray-200';
    const percentage = (current / max) * 100;
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getUtilizationPercentage = (current, max) => {
    if (!max || max === 0) return 0;
    return Math.min(Math.round((current / max) * 100), 100);
  };

  const filteredWarehouses = warehouses.filter(warehouse => {
    const matchesStatus = filterStatus === 'all' || warehouse.status === filterStatus;
    const matchesBranch = filterBranch === 'all' || warehouse.branchId === parseInt(filterBranch);
    const matchesType = filterType === 'all' || warehouse.warehouseType === filterType;
    const matchesSearch = warehouse.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         warehouse.code.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesBranch && matchesType && matchesSearch;
  });

  const handleCreate = () => {
    setEditingWarehouse(null);
    setFormData({
      code: '',
      name: '',
      branchId: '',
      warehouseType: 'GENERAL',
      status: 'ACTIVE',
      description: '',
      capacity: '',
      capacityUnit: 'UNITS',
      currentUtilization: '',
      managerName: '',
      managerEmail: '',
      managerPhone: '',
      notes: ''
    });
    setShowModal(true);
  };

  const handleEdit = (warehouse) => {
    setEditingWarehouse(warehouse);
    setFormData({
      code: warehouse.code || '',
      name: warehouse.name || '',
      branchId: warehouse.branchId || '',
      warehouseType: warehouse.warehouseType || 'GENERAL',
      status: warehouse.status || 'ACTIVE',
      description: warehouse.description || '',
      capacity: warehouse.capacity || '',
      capacityUnit: warehouse.capacityUnit || 'UNITS',
      currentUtilization: warehouse.currentUtilization || '',
      managerName: warehouse.managerName || '',
      managerEmail: warehouse.managerEmail || '',
      managerPhone: warehouse.managerPhone || '',
      notes: warehouse.notes || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingWarehouse) {
        await warehouseService.updateWarehouse(editingWarehouse.id, formData);
      } else {
        await warehouseService.createWarehouse(formData);
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      console.error('Error saving warehouse:', error);
      alert('Failed to save warehouse. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('cmms.wh_deleteConfirm'))) {
      try {
        await warehouseService.deleteWarehouse(id);
        loadData();
      } catch (error) {
        console.error('Error deleting warehouse:', error);
        alert('Failed to delete warehouse. Please try again.');
      }
    }
  };

  // Stats
  const stats = {
    total: warehouses.length,
    active: warehouses.filter(w => w.status === 'ACTIVE').length,
    full: warehouses.filter(w => w.status === 'FULL').length,
    totalItems: warehouses.reduce((sum, w) => sum + (w.itemCount || 0), 0)
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('cmms.wh_title')}</h1>
          <p className="text-gray-600 mt-1">{t('cmms.wh_subtitle')}</p>
        </div>
        <button
          onClick={handleCreate}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-5 h-5 mr-2" />
          {t('cmms.wh_addWarehouse')}
        </button>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <WarehouseIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
              <div className="text-sm text-gray-600">{t('cmms.wh_totalWarehouses')}</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">{stats.active}</div>
              <div className="text-sm text-gray-600">{t('common.active')}</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-yellow-600">{stats.full}</div>
              <div className="text-sm text-gray-600">{t('cmms.wh_atCapacity')}</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-600">{stats.totalItems.toLocaleString()}</div>
              <div className="text-sm text-gray-600">{t('cmms.wh_totalItems')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[250px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder={t('cmms.wh_searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <select
            value={filterBranch}
            onChange={(e) => setFilterBranch(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">{t('cmms.wh_allBranches')}</option>
            {branches.map(branch => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">{t('cmms.wh_allTypes')}</option>
            {warehouseTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">{t('cmms.wh_allStatus')}</option>
              {statusOptions.map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded ${viewMode === 'table' ? 'bg-white shadow-sm' : ''}`}
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Warehouses List */}
      {viewMode === 'table' ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('cmms.wh_title')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.branch')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('cmms.wh_warehouseType')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.status')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('cmms.wh_capacity')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('cmms.wh_items')}</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredWarehouses.map((warehouse) => {
                  const statusBadge = getStatusBadge(warehouse.status);
                  const StatusIcon = statusBadge.icon;
                  const utilizationPct = getUtilizationPercentage(warehouse.currentUtilization, warehouse.capacity);

                  return (
                    <tr key={warehouse.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                            <WarehouseIcon className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{warehouse.name}</div>
                            <div className="text-xs text-gray-500">{warehouse.code}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Building className="w-4 h-4 text-gray-400" />
                          <div>
                            <div className="text-sm text-gray-900">{warehouse.branchName}</div>
                            <div className="text-xs text-gray-500">{warehouse.branchCode}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getTypeBadge(warehouse.warehouseType)}`}>
                          {getTypeLabel(warehouse.warehouseType)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${statusBadge.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusBadge.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {warehouse.capacity ? (
                          <div className="space-y-1">
                            <div className="flex justify-between text-xs">
                              <span className="text-gray-600">
                                {warehouse.currentUtilization?.toLocaleString() || 0} / {warehouse.capacity.toLocaleString()} {warehouse.capacityUnit}
                              </span>
                              <span className="font-medium">{utilizationPct}%</span>
                            </div>
                            <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className={`h-full ${getUtilizationColor(warehouse.currentUtilization, warehouse.capacity)}`}
                                style={{ width: `${utilizationPct}%` }}
                              />
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {warehouse.itemCount?.toLocaleString() || 0}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => setViewingWarehouse(warehouse)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(warehouse)}
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(warehouse.id)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              {t('cmms.wh_showingOf', { filtered: filteredWarehouses.length, total: warehouses.length })}
            </div>
          </div>
        </div>
      ) : (
        /* Grid View */
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredWarehouses.map((warehouse) => {
            const statusBadge = getStatusBadge(warehouse.status);
            const StatusIcon = statusBadge.icon;
            const utilizationPct = getUtilizationPercentage(warehouse.currentUtilization, warehouse.capacity);

            return (
              <div key={warehouse.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <WarehouseIcon className="w-6 h-6 text-indigo-600" />
                  </div>
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${statusBadge.color}`}>
                    <StatusIcon className="w-3 h-3" />
                    {statusBadge.label}
                  </span>
                </div>

                <h3 className="text-lg font-bold text-gray-900 mb-1">{warehouse.name}</h3>
                <p className="text-sm text-gray-500 mb-3">{warehouse.code}</p>

                <div className="flex items-center gap-2 mb-4">
                  <Building className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">{warehouse.branchName}</span>
                </div>

                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getTypeBadge(warehouse.warehouseType)} mb-4`}>
                  {getTypeLabel(warehouse.warehouseType)}
                </span>

                {warehouse.capacity && (
                  <div className="mb-4">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-600">{t('cmms.wh_capacity')}</span>
                      <span className="font-medium">{utilizationPct}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${getUtilizationColor(warehouse.currentUtilization, warehouse.capacity)}`}
                        style={{ width: `${utilizationPct}%` }}
                      />
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {warehouse.currentUtilization?.toLocaleString() || 0} / {warehouse.capacity.toLocaleString()} {warehouse.capacityUnit}
                    </div>
                  </div>
                )}

                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 flex items-center gap-1">
                      <Package className="w-3 h-3" />
                      {t('cmms.wh_items')}:
                    </span>
                    <span className="font-medium text-gray-900">{warehouse.itemCount?.toLocaleString() || 0}</span>
                  </div>
                  {warehouse.managerName && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {t('cmms.wh_manager')}:
                      </span>
                      <span className="font-medium text-gray-900">{warehouse.managerName}</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 pt-4 border-t border-gray-200">
                  <button onClick={() => setViewingWarehouse(warehouse)} className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
                    <Eye className="w-4 h-4 mr-2" />
                    {t('common.view')}
                  </button>
                  <button
                    onClick={() => handleEdit(warehouse)}
                    className="flex-1 inline-flex items-center justify-center px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    {t('common.edit')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* View Modal */}
      {viewingWarehouse && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <WarehouseIcon className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{viewingWarehouse.name}</h2>
                  <p className="text-sm text-gray-500">{viewingWarehouse.code}</p>
                </div>
              </div>
              <button onClick={() => setViewingWarehouse(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase mb-1">{t('cmms.wh_warehouseType')}</div>
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getTypeBadge(viewingWarehouse.warehouseType)}`}>
                    {getTypeLabel(viewingWarehouse.warehouseType)}
                  </span>
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase mb-1">{t('common.status')}</div>
                  {(() => { const s = getStatusBadge(viewingWarehouse.status); const SI = s.icon; return (
                    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${s.color}`}>
                      <SI className="w-3 h-3" />{s.label}
                    </span>
                  ); })()}
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase mb-1">{t('common.branch')}</div>
                  <div className="text-sm text-gray-900">{viewingWarehouse.branchName || '—'}</div>
                  <div className="text-xs text-gray-500">{viewingWarehouse.branchCode}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase mb-1">{t('cmms.wh_items')}</div>
                  <div className="text-sm text-gray-900">{viewingWarehouse.itemCount?.toLocaleString() || 0}</div>
                </div>
              </div>
              {viewingWarehouse.capacity > 0 && (
                <div className="border-t border-gray-100 pt-4">
                  <div className="text-xs font-medium text-gray-500 uppercase mb-2">{t('cmms.wh_capabilitySection')}</div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">
                      {viewingWarehouse.currentUtilization?.toLocaleString() || 0} / {viewingWarehouse.capacity?.toLocaleString()} {viewingWarehouse.capacityUnit}
                    </span>
                    <span className="font-medium">{getUtilizationPercentage(viewingWarehouse.currentUtilization, viewingWarehouse.capacity)}%</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${getUtilizationColor(viewingWarehouse.currentUtilization, viewingWarehouse.capacity)}`}
                      style={{ width: `${getUtilizationPercentage(viewingWarehouse.currentUtilization, viewingWarehouse.capacity)}%` }}
                    />
                  </div>
                </div>
              )}
              {viewingWarehouse.managerName && (
                <div className="border-t border-gray-100 pt-4">
                  <div className="text-xs font-medium text-gray-500 uppercase mb-2">{t('cmms.wh_managerSection')}</div>
                  <div className="text-sm text-gray-900">{viewingWarehouse.managerName}</div>
                  {viewingWarehouse.managerEmail && <div className="text-xs text-gray-500">{viewingWarehouse.managerEmail}</div>}
                  {viewingWarehouse.managerPhone && <div className="text-xs text-gray-500">{viewingWarehouse.managerPhone}</div>}
                </div>
              )}
              {viewingWarehouse.description && (
                <div className="border-t border-gray-100 pt-4">
                  <div className="text-xs font-medium text-gray-500 uppercase mb-1">{t('common.description')}</div>
                  <div className="text-sm text-gray-700">{viewingWarehouse.description}</div>
                </div>
              )}
              {viewingWarehouse.notes && (
                <div className="border-t border-gray-100 pt-4">
                  <div className="text-xs font-medium text-gray-500 uppercase mb-1">{t('common.notes')}</div>
                  <div className="text-sm text-gray-700">{viewingWarehouse.notes}</div>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setViewingWarehouse(null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => { setViewingWarehouse(null); handleEdit(viewingWarehouse); }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {t('common.edit')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingWarehouse ? t('cmms.wh_editWarehouse') : t('cmms.wh_addWarehouse')}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.code')} *</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                    placeholder="WH-001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.name')} *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              {/* Branch Selection */}
              <div className="bg-blue-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-blue-900 mb-2">
                  <Building className="w-4 h-4 inline mr-2" />
                  {t('common.branch')} *
                </label>
                <select
                  value={formData.branchId}
                  onChange={(e) => setFormData({...formData, branchId: e.target.value})}
                  className="w-full px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                  required
                >
                  <option value="">Select Branch</option>
                  {branches.map(branch => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name} ({branch.code})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-blue-600 mt-1">
                  {t('cmms.wh_branchRequired')}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.wh_warehouseType')} *</label>
                  <select
                    value={formData.warehouseType}
                    onChange={(e) => setFormData({...formData, warehouseType: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    {warehouseTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.status')}</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {statusOptions.map(status => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Capacity */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">{t('cmms.wh_capabilitySection')}</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.wh_capacity')}</label>
                    <input
                      type="number"
                      value={formData.capacity}
                      onChange={(e) => setFormData({...formData, capacity: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="10000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.wh_unit')}</label>
                    <select
                      value={formData.capacityUnit}
                      onChange={(e) => setFormData({...formData, capacityUnit: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {capacityUnits.map(unit => (
                        <option key={unit.value} value={unit.value}>{unit.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.wh_current')}</label>
                    <input
                      type="number"
                      value={formData.currentUtilization}
                      onChange={(e) => setFormData({...formData, currentUtilization: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>

              {/* Manager Info */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">{t('cmms.wh_managerSection')}</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.wh_managerName')}</label>
                    <input
                      type="text"
                      value={formData.managerName}
                      onChange={(e) => setFormData({...formData, managerName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.wh_managerEmail')}</label>
                    <input
                      type="email"
                      value={formData.managerEmail}
                      onChange={(e) => setFormData({...formData, managerEmail: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.wh_managerPhone')}</label>
                    <input
                      type="tel"
                      value={formData.managerPhone}
                      onChange={(e) => setFormData({...formData, managerPhone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.description')}</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.notes')}</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingWarehouse ? t('cmms.wh_updateWarehouse') : t('cmms.wh_createWarehouse')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Warehouses;
